import * as React from "react";
import { Button, type ButtonProps } from "@/components/ui/button";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";


type GetStartedButtonProps = ButtonProps & {
  iconSize?: number;
  iconStrokeWidth?: number;
};

const GetStartedButton = React.forwardRef<
  HTMLButtonElement,
  GetStartedButtonProps
>((props, ref) => {
  const {
    className,
    size = "default",
    children = "Get Started",
    iconSize = 16,
    iconStrokeWidth = 2,
    onClick,
    ...restProps
  } = props;

  const [isHovered, setIsHovered] = React.useState(false);
  const [isClicked, setIsClicked] = React.useState(false);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setIsClicked(true);
    
    setTimeout(() => {
      setIsClicked(false);
      if (onClick) {
        onClick(e);
      }
    }, 600);
  };

  return (
    <Button
      ref={ref}
      size={size}
      variant="default"
      className={cn("group relative overflow-hidden", className)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
      {...restProps}
    >
      <motion.span
        animate={{
          opacity: isHovered ? 0 : 1,
          x: isHovered ? -10 : 0
        }}
        transition={{
          duration: 0.3,
          ease: "easeInOut"
        }}
        className="mr-8"
      >
        {children}
      </motion.span>
      
      <motion.span
        className="absolute right-1 top-1 bottom-1 rounded-lg z-10 flex items-center justify-center bg-[#fac644]"
        animate={{
          width: isHovered ? "calc(100% - 0.5rem)" : "25%",
          scale: isClicked ? 0.9 : 1
        }}
        transition={{
          duration: 0.4,
          ease: [0.4, 0, 0.2, 1]
        }}
        whileTap={{ scale: 0.95 }}
        aria-hidden="true"
      >
        <motion.div
          animate={{
            x: isHovered ? 4 : 0,
            rotate: isHovered ? 5 : 0
          }}
          transition={{
            duration: 0.3,
            ease: "easeInOut",
            delay: isHovered ? 0.1 : 0
          }}
        >
          <ChevronRight size={iconSize} strokeWidth={iconStrokeWidth} />
        </motion.div>
      </motion.span>
    </Button>
  );
});

GetStartedButton.displayName = "GetStartedButton";

export default GetStartedButton;
